
import React, { useRef, useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Plus, Minus } from 'lucide-react';
import { motion } from 'framer-motion';

// --- Custom Hook for Long Press ---
export const useLongPress = (
  callback: () => void,
  ms: number = 800
) => {
  const [startLongPress, setStartLongPress] = useState(false);
  const timerId = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (startLongPress) {
      timerId.current = setTimeout(callback, ms);
    } else {
      clearTimeout(timerId.current);
    }

    return () => {
      clearTimeout(timerId.current);
    };
  }, [startLongPress, callback, ms]);

  const start = () => setStartLongPress(true);
  const stop = () => setStartLongPress(false);

  return {
    onMouseDown: start,
    onMouseUp: stop,
    onMouseLeave: stop,
    onTouchStart: start,
    onTouchEnd: stop,
  };
};

// --- Swipe Number Input Component ---
interface SwipeNumberInputProps {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  label?: string;
  step?: number;
  format?: (val: number) => string;
  unit?: string;
}

export const SwipeNumberInput: React.FC<SwipeNumberInputProps> = ({
  value,
  onChange,
  min = 0,
  max = 1440,
  label,
  step = 1,
  format = (v) => v.toString(),
  unit
}) => {
  const startY = useRef<number | null>(null);
  const currentValRef = useRef(value);
  const [isDragging, setIsDragging] = useState(false);

  // Sync ref with props
  useEffect(() => {
    currentValRef.current = value;
  }, [value]);

  const handleStart = (clientY: number) => {
    startY.current = clientY;
    setIsDragging(true);
  };

  const handleMove = (clientY: number) => {
    if (startY.current === null) return;
    
    const deltaY = startY.current - clientY;
    const sensitivity = 30; // pixels to move 1 step

    if (Math.abs(deltaY) > sensitivity) {
      const steps = Math.floor(deltaY / sensitivity);
      if (steps !== 0) {
        let nextVal = Number(currentValRef.current) + (steps * Number(step));
        if (min !== undefined) nextVal = Math.max(min, nextVal);
        if (max !== undefined) nextVal = Math.min(max, nextVal);

        if (nextVal !== currentValRef.current) {
          onChange(nextVal);
          currentValRef.current = nextVal;
          startY.current = clientY; // Reset anchor to allow continuous scrolling
        }
      }
    }
  };

  const handleEnd = () => {
    startY.current = null;
    setIsDragging(false);
  };

  // Button Handlers
  const handleIncrement = (e: React.SyntheticEvent) => {
    e.stopPropagation(); // Prevent swipe start
    e.preventDefault();
    const nextVal = Number(value) + Number(step);
    if (max !== undefined && nextVal > max) return;
    onChange(nextVal);
  };

  const handleDecrement = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const nextVal = Number(value) - Number(step);
    if (min !== undefined && nextVal < min) return;
    onChange(nextVal);
  };

  return (
    <div className="flex flex-col items-center select-none touch-none w-full">
      {label && <span className="text-xs font-bold text-stone-500 mb-1">{label}</span>}
      
      <div className="flex items-center gap-2 w-full">
         {/* Decrement Button */}
         <button
          type="button"
          onMouseDown={handleDecrement}
          onTouchStart={handleDecrement}
          className="w-12 h-16 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-500 flex items-center justify-center transition-colors active:scale-95"
          title="減らす"
        >
          <Minus size={24} />
        </button>

        {/* Swipe Area */}
        <motion.div
          whileTap={{ scale: 0.98 }}
          className={`
            relative flex-1 h-20 rounded-xl bg-stone-50 border border-stone-200 
            flex flex-col items-center justify-center cursor-ns-resize overflow-hidden
            ${isDragging ? 'ring-2 ring-orange-100 bg-orange-50/30' : ''}
          `}
          onTouchStart={(e) => handleStart(e.touches[0].clientY)}
          onTouchMove={(e) => handleMove(e.touches[0].clientY)}
          onTouchEnd={handleEnd}
          onMouseDown={(e) => handleStart(e.clientY)}
          onMouseMove={(e) => isDragging && handleMove(e.clientY)}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
        >
          {/* Visual Indicators */}
          <ChevronUp size={16} className="text-stone-300 absolute top-1 z-10 pointer-events-none" />
          
          <div className="flex items-baseline gap-1 z-10 pointer-events-none">
             <span className="text-2xl font-bold text-stone-700 font-mono">
                {format(value)}
             </span>
             {unit && <span className="text-sm font-bold text-stone-400">{unit}</span>}
          </div>
          
          <ChevronDown size={16} className="text-stone-300 absolute bottom-1 z-10 pointer-events-none" />
          
          {/* Subtle indicator of interaction */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-10 pointer-events-none">
            <div className="w-1 h-1 bg-stone-400 rounded-full" />
            <div className="w-1 h-1 bg-stone-400 rounded-full" />
            <div className="w-1 h-1 bg-stone-400 rounded-full" />
          </div>
        </motion.div>

        {/* Increment Button */}
        <button
          type="button"
          onMouseDown={handleIncrement}
          onTouchStart={handleIncrement}
          className="w-12 h-16 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-500 flex items-center justify-center transition-colors active:scale-95"
          title="増やす"
        >
          <Plus size={24} />
        </button>
      </div>
    </div>
  );
};
